import type { RoleplayRealtimeConfig } from 'services/api/roleplayClient';

const ELEVENLABS_TTS_ENDPOINT = 'wss://api.elevenlabs.io/v1/text-to-speech';
const ROLEPLAY_SAMPLE_RATE = 24000;
export const MAX_HELD_AUDIO_SECONDS = 60;
const KEEPALIVE_INTERVAL_MS = 150_000;
const MIN_CLAUSE_CHARS = 32;
const MIN_CLAUSE_REMAINDER_CHARS = 24;
const MAX_PHRASE_CHARS = 120;
const KEEPALIVE_CONTEXT_ID = 'wisefriend_roleplay_keepalive';

type ElevenLabsConfig = NonNullable<RoleplayRealtimeConfig['elevenlabs']>;

export type ElevenLabsPlaybackFailure = {
  generation: number | null;
  playbackStarted: boolean;
};

export type ElevenLabsTimingMetrics = Partial<Record<
  'elevenlabs_connection_ms'
  | 'elevenlabs_text_buffer_ms'
  | 'elevenlabs_first_audio_ms'
  | 'elevenlabs_audio_to_playback_ms'
  | 'elevenlabs_max_audio_gap_ms',
  number
>>;

export type ElevenLabsRoleplayCallbacks = {
  onConnectionOpen: (connectionMs: number) => void;
  onPlaybackStart: (generation: number) => void;
  onPlaybackDrain: (generation: number) => void;
  onFailure: (failure: ElevenLabsPlaybackFailure) => void;
  onTiming?: (generation: number, metrics: ElevenLabsTimingMetrics) => void;
};

const takeAtBoundary = (buffer: string, boundary: number) => ({
  chunk: buffer.slice(0, boundary),
  remainder: buffer.slice(boundary),
});

const firstMatchEnd = (buffer: string, pattern: RegExp, minimumEnd = 0) => {
  pattern.lastIndex = 0;
  let match = pattern.exec(buffer);
  while (match) {
    const end = match.index + match[0].length;
    if (end >= minimumEnd) return end;
    match = pattern.exec(buffer);
  }
  return -1;
};

const wordBoundaryAtOrBefore = (buffer: string, limit: number) => {
  const prefix = buffer.slice(0, limit);
  return Math.max(
    prefix.lastIndexOf(' ') + 1,
    prefix.lastIndexOf('\n') + 1,
    prefix.lastIndexOf('\t') + 1,
  );
};

export const takeSpeakablePhrase = (buffer: string) => {
  const strongBoundary = firstMatchEnd(
    buffer,
    /[.!?](?:["'\u2019\u201d)\]]*)\s+/g,
  );
  if (strongBoundary > 0 && strongBoundary <= MAX_PHRASE_CHARS) {
    return takeAtBoundary(buffer, strongBoundary);
  }

  const clauseBoundary = firstMatchEnd(
    buffer,
    /(?:[,;:]|\s[-\u2013\u2014])(?:["'\u2019\u201d)\]]*)\s+/g,
    MIN_CLAUSE_CHARS,
  );
  if (
    clauseBoundary > 0
    && clauseBoundary <= MAX_PHRASE_CHARS
    && buffer.length - clauseBoundary >= MIN_CLAUSE_REMAINDER_CHARS
  ) {
    return takeAtBoundary(buffer, clauseBoundary);
  }

  if (buffer.length >= MAX_PHRASE_CHARS) {
    const maxBoundary = wordBoundaryAtOrBefore(buffer, MAX_PHRASE_CHARS);
    if (maxBoundary >= MIN_CLAUSE_CHARS) return takeAtBoundary(buffer, maxBoundary);
  }

  return {
    chunk: '',
    remainder: buffer,
  };
};

const decodePcm24000 = (encoded: string, context: AudioContext) => {
  const bytes = Uint8Array.from(window.atob(encoded), (value) => value.charCodeAt(0));
  const frameCount = Math.floor(bytes.byteLength / 2);
  const samples = new Float32Array(frameCount);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let index = 0; index < frameCount; index += 1) {
    samples[index] = view.getInt16(index * 2, true) / 32768;
  }
  const buffer = context.createBuffer(1, frameCount, ROLEPLAY_SAMPLE_RATE);
  buffer.copyToChannel(samples, 0);
  return buffer;
};

export class ElevenLabsRoleplayTtsSession {
  private readonly config: ElevenLabsConfig;

  private readonly callbacks: ElevenLabsRoleplayCallbacks;

  private socket: WebSocket | null = null;

  private context: AudioContext | null = null;

  private gain: GainNode | null = null;

  private keepalive: number | null = null;

  private activeSources = new Set<AudioBufferSourceNode>();

  private nextPlaybackAt = 0;

  private generation: number | null = null;

  private contextSequence = 0;

  private activeContextId: string | null = null;

  private activeContextInitialized = false;

  private activeContextClosing = false;

  private textBuffer = '';

  private textFlushed = false;

  private finalAudioReceived = false;

  private playbackStarted = false;

  private playbackHeld = false;

  private heldBuffers: AudioBuffer[] = [];

  private heldFrames = 0;

  private closedIntentionally = false;

  private connectionMs: number | null = null;

  private timing: ElevenLabsTimingMetrics = {};

  private firstTextAt: number | null = null;

  private firstTextSentAt: number | null = null;

  private firstAudioAt: number | null = null;

  private lastAudioAt: number | null = null;

  constructor(config: ElevenLabsConfig, callbacks: ElevenLabsRoleplayCallbacks) {
    this.config = config;
    this.callbacks = callbacks;
  }

  async connect(muted: boolean) {
    const startedAt = performance.now();
    this.context = new AudioContext({ sampleRate: ROLEPLAY_SAMPLE_RATE });
    const audioReady = this.context.resume();
    this.gain = this.context.createGain();
    this.gain.gain.value = muted ? 0 : 1;
    this.gain.connect(this.context.destination);
    const query = new URLSearchParams({
      model_id: this.config.model_id,
      output_format: this.config.output_format,
      auto_mode: 'true',
      inactivity_timeout: '180',
      single_use_token: this.config.single_use_token,
    });
    const url = `${ELEVENLABS_TTS_ENDPOINT}/${encodeURIComponent(this.config.voice_id)}`
      + `/multi-stream-input?${query.toString()}`;

    const socketReady = new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(url);
      this.socket = socket;
      const rejectConnection = () => reject(new Error('ElevenLabs voice connection failed.'));
      socket.onerror = rejectConnection;
      socket.onclose = rejectConnection;
      socket.onopen = () => {
        if (this.socket !== socket || this.closedIntentionally) {
          socket.close();
          rejectConnection();
          return;
        }
        socket.onerror = () => this.handleFailure();
        socket.onclose = () => this.handleFailure();
        socket.onmessage = (event) => this.handleMessage(event);
        socket.send(JSON.stringify({
          context_id: KEEPALIVE_CONTEXT_ID,
          text: ' ',
          voice_settings: this.voiceSettings(),
        }));
        this.keepalive = window.setInterval(() => {
          if (this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify({
              context_id: KEEPALIVE_CONTEXT_ID,
              text: '',
            }));
          }
        }, KEEPALIVE_INTERVAL_MS);
        this.connectionMs = Math.max(0, performance.now() - startedAt);
        this.callbacks.onConnectionOpen(this.connectionMs);
        resolve();
      };
    });
    await Promise.all([audioReady, socketReady]);
  }

  startTurn(generation: number, options: { held?: boolean } = {}) {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.clearPlayback();
    this.generation = generation;
    this.playbackHeld = options.held === true;
    this.contextSequence += 1;
    this.activeContextId = `roleplay_${generation}_${this.contextSequence}`;
    this.activeContextInitialized = false;
    this.activeContextClosing = false;
    this.textBuffer = '';
    this.textFlushed = false;
    this.finalAudioReceived = false;
    this.playbackStarted = false;
    this.nextPlaybackAt = this.context?.currentTime ?? 0;
    if (this.connectionMs !== null) {
      this.timing.elevenlabs_connection_ms = this.connectionMs;
      this.emitTiming();
    }
    return true;
  }

  release(generation: number) {
    if (generation !== this.generation) return false;
    if (!this.playbackHeld) return true;
    this.playbackHeld = false;
    const buffers = this.heldBuffers;
    this.heldBuffers = [];
    this.heldFrames = 0;
    for (const buffer of buffers) {
      if (generation !== this.generation) return false;
      try {
        this.scheduleBuffer(buffer);
      } catch {
        this.handleFailure();
        return false;
      }
    }
    this.maybeDrain();
    return true;
  }

  appendText(generation: number, delta: string) {
    if (generation !== this.generation || this.textFlushed || !delta) return false;
    if (this.firstTextAt === null && delta.trim()) this.firstTextAt = performance.now();
    this.textBuffer += delta;
    this.drainSpeakablePhrases();
    return true;
  }

  finishText(generation: number) {
    if (generation !== this.generation || this.textFlushed) return false;
    this.textFlushed = true;
    const remainder = this.textBuffer;
    this.textBuffer = '';
    this.sendText(remainder ? `${remainder} ` : ' ', true);
    this.closeActiveContext();
    this.maybeDrain();
    return true;
  }

  setMuted(muted: boolean) {
    if (this.gain) this.gain.gain.value = muted ? 0 : 1;
  }

  clearPlayback() {
    this.closeActiveContext();
    this.activeSources.forEach((source) => {
      source.onended = null;
      try {
        source.stop();
      } catch {
        // A source that already ended is already absent from audible playback.
      }
    });
    this.activeSources.clear();
    this.nextPlaybackAt = this.context?.currentTime ?? 0;
    this.generation = null;
    this.activeContextId = null;
    this.activeContextInitialized = false;
    this.activeContextClosing = false;
    this.textBuffer = '';
    this.textFlushed = false;
    this.finalAudioReceived = false;
    this.playbackStarted = false;
    this.playbackHeld = false;
    this.heldBuffers = [];
    this.heldFrames = 0;
    this.timing = {};
    this.firstTextAt = null;
    this.firstTextSentAt = null;
    this.firstAudioAt = null;
    this.lastAudioAt = null;
  }

  close() {
    this.closedIntentionally = true;
    if (this.keepalive !== null) window.clearInterval(this.keepalive);
    this.keepalive = null;
    this.clearPlayback();
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ close_socket: true }));
    }
    if (socket && socket.readyState !== WebSocket.CLOSED) socket.close();
    this.gain?.disconnect();
    this.gain = null;
    const context = this.context;
    this.context = null;
    if (context && context.state !== 'closed') void context.close();
  }

  private sendText(text: string, flush: boolean) {
    const contextId = this.activeContextId;
    if (this.socket?.readyState !== WebSocket.OPEN || !contextId) return;
    const sentAt = performance.now();
    this.socket.send(JSON.stringify({
      context_id: contextId,
      text,
      ...(!this.activeContextInitialized ? { voice_settings: this.voiceSettings() } : {}),
      ...(flush ? { flush: true } : {}),
    }));
    this.activeContextInitialized = true;
    // Initialization/flush whitespace is not a synthesis request.
    if (this.firstTextSentAt === null && text.trim()) {
      this.firstTextSentAt = sentAt;
      if (this.firstTextAt !== null) {
        this.timing.elevenlabs_text_buffer_ms = Math.max(0, sentAt - this.firstTextAt);
      }
      this.emitTiming();
    }
  }

  private emitTiming() {
    if (this.generation === null) return;
    try {
      this.callbacks.onTiming?.(this.generation, { ...this.timing });
    } catch {
      // Optional diagnostics must never interrupt synthesis or playback.
    }
  }

  private closeActiveContext() {
    const contextId = this.activeContextId;
    if (
      this.activeContextClosing
      || !contextId
      || this.socket?.readyState !== WebSocket.OPEN
    ) return;
    this.activeContextClosing = true;
    this.socket.send(JSON.stringify({ context_id: contextId, close_context: true }));
  }

  private voiceSettings() {
    return {
      stability: 0.5,
      similarity_boost: 0.75,
      speed: 1,
      style: 0,
      use_speaker_boost: false,
    };
  }

  private drainSpeakablePhrases() {
    let next = takeSpeakablePhrase(this.textBuffer);
    while (next.chunk) {
      this.textBuffer = next.remainder;
      this.sendText(next.chunk, false);
      next = takeSpeakablePhrase(this.textBuffer);
    }
  }

  private handleMessage(event: MessageEvent) {
    if (typeof event.data !== 'string') return;
    let payload: {
      audio?: unknown;
      contextId?: unknown;
      context_id?: unknown;
      isFinal?: unknown;
      is_final?: unknown;
    };
    try {
      payload = JSON.parse(event.data) as typeof payload;
    } catch {
      this.handleFailure();
      return;
    }
    const contextId = typeof payload.contextId === 'string'
      ? payload.contextId
      : typeof payload.context_id === 'string'
        ? payload.context_id
        : '';
    if (!contextId || contextId !== this.activeContextId) return;
    try {
      if (typeof payload.audio === 'string' && payload.audio) this.scheduleAudio(payload.audio);
    } catch {
      this.handleFailure();
      return;
    }
    if (payload.isFinal === true || payload.is_final === true) {
      this.finalAudioReceived = true;
      if (!this.playbackStarted && this.heldBuffers.length === 0) {
        this.handleFailure();
        return;
      }
      this.maybeDrain();
    }
  }

  private scheduleAudio(encoded: string) {
    const context = this.context;
    if (!context || this.generation === null) return;
    const receivedAt = performance.now();
    // Reject an oversized provider chunk before decoding or retaining it.
    if (encoded.length > Math.ceil(ROLEPLAY_SAMPLE_RATE * MAX_HELD_AUDIO_SECONDS * 2 / 3) * 4) {
      throw new Error('Roleplay audio buffer exceeded its limit.');
    }
    const buffer = decodePcm24000(encoded, context);
    if (!buffer.length) return;
    let timingChanged = false;
    if (this.firstAudioAt === null) {
      this.firstAudioAt = receivedAt;
      if (this.firstTextSentAt !== null) {
        // Includes provider synthesis and both transport directions, not network RTT.
        this.timing.elevenlabs_first_audio_ms = Math.max(0, receivedAt - this.firstTextSentAt);
        timingChanged = true;
      }
    }
    if (this.lastAudioAt !== null) {
      const gap = Math.max(0, receivedAt - this.lastAudioAt);
      if (gap > (this.timing.elevenlabs_max_audio_gap_ms ?? -1)) {
        this.timing.elevenlabs_max_audio_gap_ms = gap;
        timingChanged = true;
      }
    }
    this.lastAudioAt = receivedAt;
    if (timingChanged) this.emitTiming();
    if (this.playbackHeld) {
      if (
        this.heldFrames + buffer.length > ROLEPLAY_SAMPLE_RATE * MAX_HELD_AUDIO_SECONDS
        || this.heldBuffers.length >= 4096
      ) {
        throw new Error('Roleplay audio buffer exceeded its limit.');
      }
      this.heldFrames += buffer.length;
      this.heldBuffers.push(buffer);
      return;
    }
    this.scheduleBuffer(buffer);
  }

  private scheduleBuffer(buffer: AudioBuffer) {
    const context = this.context;
    const gain = this.gain;
    const generation = this.generation;
    if (!context || !gain || generation === null) return;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(gain);
    const startAt = Math.max(context.currentTime + 0.01, this.nextPlaybackAt);
    this.nextPlaybackAt = startAt + buffer.duration;
    this.activeSources.add(source);
    source.onended = () => {
      this.activeSources.delete(source);
      this.maybeDrain();
    };
    // A failed Web Audio start is still a retryable, unheard directive.
    source.start(startAt);
    if (!this.playbackStarted) {
      this.playbackStarted = true;
      this.callbacks.onPlaybackStart(generation);
    }
    if (this.firstAudioAt !== null && this.timing.elevenlabs_audio_to_playback_ms === undefined) {
      // Scheduled Web Audio start includes decoding, approval hold, and queue lead-in.
      this.timing.elevenlabs_audio_to_playback_ms = Math.max(
        0,
        performance.now() - this.firstAudioAt + (startAt - context.currentTime) * 1000,
      );
      this.emitTiming();
    }
  }

  private maybeDrain() {
    const generation = this.generation;
    if (
      !this.textFlushed
      || this.playbackHeld
      || this.heldBuffers.length > 0
      || !this.playbackStarted
      || this.activeSources.size > 0
      || generation === null
    ) return;

    if (!this.finalAudioReceived) return;

    this.completeDrain(generation);
  }

  private completeDrain(generation: number) {
    if (this.generation !== generation) return;
    // Clear every generation-scoped flag before a callback may start another
    // turn or an idle socket failure can be mistaken for partial playback.
    this.clearPlayback();
    this.callbacks.onPlaybackDrain(generation);
  }

  private handleFailure() {
    if (this.closedIntentionally || !this.socket) return;
    const generation = this.generation;
    const playbackStarted = this.playbackStarted;
    const socket = this.socket;
    this.socket = null;
    if (this.keepalive !== null) window.clearInterval(this.keepalive);
    this.keepalive = null;
    socket.onclose = null;
    if (socket.readyState !== WebSocket.CLOSED) socket.close();
    this.clearPlayback();
    this.callbacks.onFailure({ generation, playbackStarted });
  }
}
